// v0.70.18 — a device-side cache of other people's public profiles (their own
// name + emoji), so anything that labels a person can fall back to THEIR name
// before the M-link slug. Refreshed opportunistically; never blocks a render.
//
//   contactLabel(): nickname → their profile name → code → short address

import { getProfile, type Profile } from "./api";

const KEY = "m.profiles";
const TTL_MS = 6 * 60 * 60 * 1000; // 6h — names change rarely; emoji picks too

type Entry = Profile & { at: number };

function readAll(): Record<string, Entry> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, Entry>;
  } catch {
    return {};
  }
}
function writeAll(all: Record<string, Entry>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* storage blocked — cosmetic */
  }
}

/** Synchronous read (may be stale or missing). */
export function cachedProfile(address: string): Profile | null {
  const e = readAll()[address.toLowerCase()];
  return e ? { code: e.code, name: e.name, emoji: e.emoji } : null;
}

export function rememberProfile(address: string, p: Profile): void {
  const all = readAll();
  all[address.toLowerCase()] = { ...p, at: Date.now() };
  writeAll(all);
}

/**
 * Refresh what's missing or stale for a set of addresses. Resolves once all
 * lookups have settled; callers re-render then if they care.
 */
export async function warmProfiles(addresses: string[], force = false): Promise<boolean> {
  const all = readAll();
  const now = Date.now();
  const todo = [...new Set(addresses.map((a) => a.toLowerCase()))].filter((a) => {
    const e = all[a];
    return force || !e || now - e.at > TTL_MS;
  });
  if (!todo.length) return false;
  let changed = false;
  await Promise.all(
    todo.map(async (a) => {
      try {
        const p = await getProfile(a);
        rememberProfile(a, p);
        changed = true;
      } catch {
        /* offline: keep what we have */
      }
    }),
  );
  return changed;
}
